import { customDisciplineLabel } from "../../../util/src/custom-disciplines/labels.js";
import { useCompiledLayoutRuntimeData } from "./CompiledLayoutRuntimeContext.js";
import {
  getTextGradientFillCss,
  hasTextGradientFill,
} from "../../../util/src/layout/text-gradient-fill.js";
import React from "react";
import type { RenderTextNode } from "../../../util/src/layout-compiler/types.js";
import { nodeStyleWithoutDimensions } from "./nodeStyleHelpers.js";
import { getRenderNodeClipStyle } from "./renderClipStyle.js";
import { RenderNodeTooltipShell } from "./RenderNodeTooltipShell.js";
import {
  nodeSpansDesignSize,
  useCompiledLayoutFill,
} from "./CompiledLayoutFillContext.js";
import { useRuntimeNodeProps } from "./useRuntimeElementRef.js";

type RenderTextNodeProps = {
  node: RenderTextNode;
  scale: number;
};

function scalePx(value: unknown, scale: number): unknown {
  if (typeof value === "number") {
    return value * scale;
  }

  if (typeof value === "string" && value.endsWith("px")) {
    const parsed = Number.parseFloat(value);
    if (Number.isFinite(parsed)) {
      return `${parsed * scale}px`;
    }
  }

  return value;
}

function composeTransform(
  rotation: number,
  styleTransform?: unknown,
): string | undefined {
  const rotationTransform =
    rotation !== 0 ? `rotate(${rotation}deg)` : undefined;
  const extra =
    typeof styleTransform === "string" && styleTransform.trim().length > 0
      ? styleTransform
      : undefined;

  if (rotationTransform && extra) return `${rotationTransform} ${extra}`;
  return rotationTransform ?? extra;
}

function scaleTextStyle(
  style: React.CSSProperties,
  scale: number,
): React.CSSProperties {
  const nodeStyle = nodeStyleWithoutDimensions(
    style as Record<string, unknown>,
  );
  return {
    ...nodeStyle,
    fontSize: scalePx(style.fontSize, scale),
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    justifyContent:
      style.textAlign === "center"
        ? "center"
        : style.textAlign === "right"
          ? "flex-end"
          : "flex-start",
  } as React.CSSProperties;
}

export function RenderTextNode({ node, scale }: RenderTextNodeProps) {
  const runtimeProps = useRuntimeNodeProps(node);
  const labels = useCompiledLayoutRuntimeData()?.__customDisciplineLabels;
  const fillContext = useCompiledLayoutFill();
  const isFitContentWidth = node.textWidthMode === "fitContent";
  const layoutStyle = nodeStyleWithoutDimensions(node.style);
  const clipStyle = getRenderNodeClipStyle(node, scale);
  const gradientFill = hasTextGradientFill(layoutStyle.backgroundImage)
    ? getTextGradientFillCss(String(layoutStyle.backgroundImage))
    : undefined;
  const { backgroundImage: _omitBg, ...layoutStyleWithoutGradient } =
    layoutStyle;
  const boxLayoutStyle = gradientFill
    ? layoutStyleWithoutGradient
    : layoutStyle;
  const isRelative = node.style.position === "relative";
  const fillParent = fillContext?.fillParent === true;
  const spansDesignWidth =
    fillContext != null &&
    nodeSpansDesignSize(node.width, fillContext.designWidth);
  const spansDesignHeight =
    fillContext != null &&
    nodeSpansDesignSize(node.height, fillContext.designHeight);
  const stretchInParent =
    fillParent && isRelative && spansDesignWidth && spansDesignHeight;

  return (
    <RenderNodeTooltipShell
      tooltip={node.tooltip}
      ownerNodeKey={node.key ?? node.id}
    >
      <div
        {...runtimeProps}
        style={{
          ...(isRelative
            ? {
                position: "relative",
                flexShrink: stretchInParent ? 1 : 0,
                flex: stretchInParent ? 1 : undefined,
                minWidth: stretchInParent ? 0 : undefined,
              }
            : {
                position: "absolute",
                left: node.x * scale,
                top: node.y * scale,
              }),
          width: stretchInParent
            ? "100%"
            : isFitContentWidth
              ? "fit-content"
              : node.width * scale,
          height: stretchInParent ? "100%" : node.height * scale,
          maxWidth: isFitContentWidth ? "max-content" : undefined,
          zIndex: node.zIndex,
          overflow: "hidden",
          ...scaleTextStyle(boxLayoutStyle, scale),
          transform: composeTransform(node.rotation, node.style?.transform),
          transformOrigin: "center center",
          ...(gradientFill
            ? {}
            : { color: String(node.style.color ?? "black") }),
          ...clipStyle,
        }}
      >
        <span
          style={{
            display: "inline-block",
            width: isFitContentWidth || gradientFill ? "max-content" : "100%",
            maxWidth: "100%",
            lineHeight: layoutStyle.lineHeight ?? (gradientFill ? 1 : 1.2),
            ...gradientFill,
            whiteSpace:
              layoutStyle.whiteSpace as React.CSSProperties["whiteSpace"],
            textAlign:
              (layoutStyle.textAlign as React.CSSProperties["textAlign"]) ??
              "inherit",
          }}
        >
          {node.preserveBoundText
            ? node.text
            : customDisciplineLabel(labels, node.text)}
        </span>
      </div>
    </RenderNodeTooltipShell>
  );
}
